// Set up second factors.
//
// One component, three ways in:
//   - straight after login, when the account has nothing enrolled yet and the
//     deadline has not passed (twoFactorSetupRequired, with tokens): /setup-2fa,
//     skippable;
//   - straight after login once the deadline has passed (twoFactorSetupRequired,
//     no tokens, a `challenge` instead): /login-setup-2fa. SMS only, and
//     confirming the code is what finishes the login;
//   - from the "Set up 2FA" badge in the header: /setup-2fa.
//
// A client may enrol in several methods; each is set up in place. Nothing can
// be removed here - only staff can turn a client's 2FA off, from Teams.
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import Default from "../../components/layouts/default";
import logo from "../../assets/images/adm_logo.png";
import LoginAuth from "../../services/auth.service";
import Error from "../../components/common/Error";
import Success from "../../components/common/Success";
import SmsConsentCheck from "../../components/common/SmsConsentCheck";
import ClientService from "../../services/client.service";
import { setClientId } from "../../feature/client.slice";
import { safeRedirect } from "../../utils/safeRedirect";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import {
  getTotpStatus,
  setupTotp,
  enableTotp,
  passkeyRegisterBegin,
  passkeyRegisterFinish,
  smsSetup,
  smsEnable,
} from "../../services/profile.service";

// Keyed by method. The server sends the order, so this is only labelling.
const META = {
  passkey: {
    title: "Passkey / biometric",
    blurb:
      "Sign in with your fingerprint, face or device PIN. Nothing to type, and it cannot be phished - the key only works on this site.",
    action: "Set up passkey",
    lead: "The strongest option, and the quickest to use.",
  },
  sms: {
    title: "Text message",
    blurb: "We'll text a 6-digit code to the mobile number on your account.",
    action: "Set up text message",
    lead: "Simple, and works on any phone.",
  },
  totp: {
    title: "Authenticator app",
    blurb:
      "Use Google Authenticator, Authy or a password manager to generate a 6-digit code. Works offline.",
    action: "Set up authenticator app",
    lead: "Works with no signal at all.",
  },
};

// Drawn inline rather than with an icon font: this app loads none, so a
// class-based icon would render as an empty element.
const METHOD_ICON = {
  passkey: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 11c0-1.7 1.3-3 3-3s3 1.3 3 3-1.3 3-3 3" />
      <path d="M15 14v6" /><path d="M13 17h4" />
      <path d="M9.5 21a9 9 0 0 1-1.2-10.5" />
      <path d="M6 7a8 8 0 0 1 11-1" />
    </svg>
  ),
  totp: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v5c0 4.4-3 8.3-7 10-4-1.7-7-5.6-7-10V6z" />
      <path d="M12 8v4l2.5 1.5" />
    </svg>
  ),
  sms: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.3-.6L3 21l1.8-5a8.4 8.4 0 0 1-.8-3.6 8.4 8.4 0 0 1 9-8.4 8.4 8.4 0 0 1 8 7.5z" />
    </svg>
  ),
};

const Setup2fa = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const returnTo = safeRedirect(location.state?.redirect) || "/dashboard";

  // Past the deadline there is no session yet, only the login challenge, and
  // the only thing it is good for is SMS setup.
  const challengeMode = location.pathname === "/login-setup-2fa";
  const [challenge, setChallenge] = useState(location.state?.challenge || null);
  const forced = challengeMode || !!location.state?.forced;

  // What the account holds, and what to nudge them towards next.
  const [enrolled, setEnrolled] = useState([]);
  const [order, setOrder] = useState(["sms"]);
  const [smsAvailable, setSmsAvailable] = useState(challengeMode ? !!location.state?.phone : true);
  const [passkeyCount, setPasskeyCount] = useState(0);
  const [phoneHint, setPhoneHint] = useState(location.state?.phone || "");

  // SMS needs consent to be texted at the number on file. Without it the SMS
  // row shows the consent box, and "Send code" waits until it is ticked; the
  // tick is recorded by the API before any code goes out.
  const [consentOnFile, setConsentOnFile] = useState(
    challengeMode ? !!location.state?.smsConsent : true
  );
  const [consentTicked, setConsentTicked] = useState(false);

  // Client 2FA switched off on the server (ENABLE_CLIENT_2FA): the page can
  // still be opened by URL, so it says so instead of offering a setup the API
  // would refuse. Challenge mode only exists when it is on.
  const [twoFactorOn, setTwoFactorOn] = useState(true);

  const [canSkip, setCanSkip] = useState(!challengeMode && location.state?.canSkip !== false);
  const [deadline, setDeadline] = useState(location.state?.deadline || null);

  // Whichever method is mid-enrolment, and its in-flight state.
  const [activeMethod, setActiveMethod] = useState(null);
  const [totpSetup, setTotpSetup] = useState(null); // QR + key
  const [code, setCode] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");


  const refresh = async () => {
    try {
      const d = await getTotpStatus();
      setEnrolled(d.enrolled || []);
      if (Array.isArray(d.order)) setOrder(d.order);
      if (typeof d.sms_available === "boolean") setSmsAvailable(d.sms_available);
      if (typeof d.passkey_count === "number") setPasskeyCount(d.passkey_count);
      if (typeof d.canSkip === "boolean") setCanSkip(d.canSkip);
      if (d.deadline) setDeadline(d.deadline);
      if (d.phone) setPhoneHint(d.phone);
      if (typeof d.sms_consent === "boolean") setConsentOnFile(d.sms_consent);
      if (typeof d.two_factor_on === "boolean") setTwoFactorOn(d.two_factor_on);
    } catch (e) {
      /* leave the defaults in place */
    }
  };

  useEffect(() => {
    // No session to ask with in challenge mode; the login response said all
    // there is to know.
    if (challengeMode) return;
    refresh();
    // Runs once on mount; refresh() is re-created each render and listing it
    // would re-fetch on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Challenge mode needs the challenge from the login screen. A direct visit,
  // or one whose challenge died (too many wrong codes), goes back to /login.
  useEffect(() => {
    if (challengeMode && !challenge) {
      navigate("/login", { replace: true });
    }
  }, [challengeMode, challenge, navigate]);

  const fetchClientAndStore = async (userId) => {
    try {
      const { clientIds } = await ClientService.getclient({ userId });
      localStorage.setItem("client", JSON.stringify(clientIds));
      dispatch(setClientId(clientIds[0].id));
    } catch (e) {
      /* the dashboard loads the client list itself if this fails */
    }
  };

  const clear = () => { setError(""); setSuccess(""); };


  const prettyDeadline = deadline
    ? new Date(deadline + "T00:00:00").toLocaleDateString(undefined, {
        day: "numeric", month: "long", year: "numeric",
      })
    : null;

  // Postpone. Nothing is recorded: the deadline lives on the user row, so the
  // same prompt returns at the next login until it passes.
  const handleSkip = () => navigate(returnTo, { replace: true });

  const handleBackToLogin = async () => {
    // In challenge mode there is no session to end.
    if (!challengeMode) {
      try { await LoginAuth.logout(); } catch (e) { /* log out locally regardless */ }
    }
    navigate("/login", { replace: true });
  };

  const cancelEnrolment = () => {
    setActiveMethod(null);
    setTotpSetup(null);
    setCode("");
    clear();
  };

  // ── passkey ──────────────────────────────────────────────────────────────
  // A browser ceremony: the server hands over options, the device creates a
  // key pair behind a fingerprint/face/PIN prompt, and only the public half
  // comes back. There is no code to type, so it completes in one step.
  const addPasskey = async () => {
    clear();
    if (!browserSupportsWebAuthn()) {
      setError("This browser does not support passkeys. Try another method.");
      return;
    }
    setBusy(true);
    try {
      const options = await passkeyRegisterBegin();
      const attestation = await startRegistration({ optionsJSON: options });
      await passkeyRegisterFinish(attestation, navigator.platform || "This device");
      await refresh();
      setSuccess("Passkey added. You can use it to sign in from now on.");
    } catch (err) {
      // Dismissing the system prompt lands here too - a cancellation, not a
      // failure, and it should not read like one.
      if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
        setError("Passkey setup was cancelled.");
      } else {
        setError(err.response?.data?.message || err.message || "Could not add the passkey");
      }
    } finally {
      setBusy(false);
    }
  };

  // ── text message ─────────────────────────────────────────────────────────
  const startSms = async () => {
    clear();
    if (!smsAvailable) {
      setError("No mobile number is on file for your account. Please contact your administrator.");
      return;
    }
    setBusy(true);
    try {
      const smsConsent = consentOnFile ? undefined : consentTicked;
      const res = challengeMode
        ? await LoginAuth.setupSmsSend({ challenge, smsConsent })
        : await smsSetup(smsConsent);
      setConsentOnFile(true);
      setActiveMethod("sms");
      if (res.phone) setPhoneHint(res.phone);
      setSuccess(res.message || "Code sent to your mobile.");
    } catch (err) {
      if (err.response?.data?.consentRequired) setConsentOnFile(false);
      // Already set up (e.g. finished in another tab): the code screen is the
      // way in, so back to login rather than a dead end here.
      if (challengeMode && err.response?.data?.alreadyEnrolled) setTimeout(() => setChallenge(null), 3000);
      setError(err.response?.data?.message || err.response?.data?.error || "Could not send the code");
    } finally {
      setBusy(false);
    }
  };

  const confirmSms = async () => {
    clear();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from the text message.");
      return;
    }
    setBusy(true);
    try {
      if (challengeMode) {
        // Turns SMS on and finishes the login in one go.
        const res = await LoginAuth.setupSmsConfirm({ challenge, otp: code });
        await fetchClientAndStore(res.data.user.user.id);
        navigate(returnTo, { replace: true });
        return;
      }
      await smsEnable(code);
      await refresh();
      cancelEnrolment();
      setSuccess("Text message verification added.");
    } catch (err) {
      // The server's message already says how many attempts are left.
      const data = err.response?.data || {};
      setError(data.message || data.error || "That code was not accepted");
      if (challengeMode && data.alreadyEnrolled) setTimeout(() => setChallenge(null), 3000);
      if (data.attemptsLeft === 0) {
        cancelEnrolment();
        // Before a session, the last wrong code locks the account and the
        // challenge dies with it: nothing left to do here.
        if (challengeMode) setTimeout(() => setChallenge(null), 4000);
      }
    } finally {
      setBusy(false);
    }
  };

  // ── authenticator app ────────────────────────────────────────────────────
  const startTotp = async () => {
    clear();
    setBusy(true);
    try {
      setTotpSetup(await setupTotp());
      setActiveMethod("totp");
    } catch (err) {
      setError(err.response?.data?.message || "Could not start setup");
    } finally {
      setBusy(false);
    }
  };

  const confirmTotp = async () => {
    clear();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your app.");
      return;
    }
    setBusy(true);
    try {
      await enableTotp(code);
      await refresh();
      cancelEnrolment();
      setSuccess("Authenticator app added.");
    } catch (err) {
      setError(err.response?.data?.message || "That code was not accepted");
    } finally {
      setBusy(false);
    }
  };

  const start = (method) => {
    if (method === "passkey") return addPasskey();
    if (method === "sms") return startSms();
    return startTotp();
  };

  const hasAny = enrolled.length > 0;

  // The per-method line under the status pill: how many devices, which number.
  const detailFor = (m) => {
    if (m === "passkey") {
      return passkeyCount
        ? `${passkeyCount} device${passkeyCount === 1 ? "" : "s"} connected`
        : null;
    }
    // SMS: the number is already in the row's description.
    return null;
  };

  // Wording follows how many methods are offered: "at least one of these"
  // reads oddly with a single option on screen.
  const single = order.length === 1;

  // Just the date: the intro line above already says what this is for.
  const twoFactorNotice = !hasAny && prettyDeadline ? (
    <div
      className={`alert py-2 px-3 ${canSkip ? "alert-info" : "alert-warning"}`}
      style={{ fontSize: "0.85rem" }}
    >
      Deadline: <strong>{prettyDeadline}</strong>
      {!canSkip && " (passed - required now)"}
    </div>
  ) : null;

  // ── Management view ───────────────────────────────────────────────────────
  // Everything is visible at once and nothing is sequential: the user picks
  // what to add or remove. The same list serves both ways in - only the
  // footer and the surrounding chrome differ.
  const manageBody = (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={9}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <h4 className="mb-1">Two-Factor Authentication</h4>
              <p className="text-muted mb-4" style={{ fontSize: "0.9rem" }}>
                {single
                  ? "Protect your account with a code sent to your mobile each time you sign in."
                  : "Add a second layer of security to your account. You can set up one or more of the options below."}
              </p>

              {twoFactorNotice}

              {!challengeMode && !twoFactorOn && (
                <div className="alert alert-secondary py-2 px-3" style={{ fontSize: "0.85rem" }}>
                  Two-factor authentication isn't available yet. We'll let you know when it is.
                </div>
              )}

              <div className="d-flex flex-column gap-3">
                {(challengeMode || twoFactorOn) && order.map((m) => {
                  const on = enrolled.includes(m);
                  const blocked = m === "sms" && !smsAvailable;
                  const open = activeMethod === m;

                  return (
                    <div key={m} className="method-card">
                      {/* A grid, not flex-wrap: with wrapping, where the
                          pill and button land depends on how long the
                          description happens to be, so no two rows line up. */}
                      <div className="method-row">
                        <div className={`method-icon method-icon-${m}`} aria-hidden="true">
                          {METHOD_ICON[m]}
                        </div>

                        <div className="method-row__text">
                          <div className="method-row__title">{META[m].title}</div>
                          <div className="method-row__blurb">
                            {/* Name the number (partly masked), so the consent
                                box below can say "the number above". */}
                            {m === "sms" && phoneHint
                              ? <>We'll text a 6-digit code to <strong className="text-nowrap">{phoneHint}</strong>.</>
                              : META[m].blurb}
                          </div>
                        </div>

                        <div className="method-row__status">
                          {/* Only once set up: a "Not set up" pill next to the
                              button that sets it up says nothing new. */}
                          {on && (
                            <span className="status-pill is-on">
                              <span className="status-dot" aria-hidden="true">✓</span>
                              Set up
                            </span>
                          )}
                          {on && detailFor(m) && (
                            <div className="method-row__detail">{detailFor(m)}</div>
                          )}
                          {blocked && !on && (
                            <div className="method-row__detail">No mobile on file</div>
                          )}
                        </div>

                        <div className="method-row__action">
                          {on ? (
                            m === "passkey" && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => start(m)}
                                disabled={busy}
                              >
                                Add another
                              </Button>
                            )
                          ) : (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => start(m)}
                              disabled={busy || blocked || (m === "sms" && !consentOnFile && !consentTicked)}
                            >
                              {m === "sms"
                                ? (busy ? "Sending..." : "Send code")
                                : (busy && open ? "Working..." : "Set up now")}
                            </Button>
                          )}
                        </div>
                      </div>

                      {m === "sms" && !on && !blocked && !consentOnFile && (
                        <div className="mt-3 pt-3 border-top">
                          <SmsConsentCheck
                            id="smsConsentSetup"
                            checked={consentTicked}
                            onChange={setConsentTicked}
                            disabled={busy}
                          />
                        </div>
                      )}

                      {/* Setup opens inside the row it belongs to, so the page
                          never jumps somewhere else mid-task. */}
                      {open && m === "sms" && (
                        <div className="mt-3 pt-3 border-top">
                          <div className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
                            Enter the code we texted to {phoneHint || "your mobile"}.
                          </div>
                          <div className="d-flex gap-2 flex-wrap align-items-center">
                            <Form.Control
                              type="text"
                              value={code}
                              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                              maxLength={6}
                              placeholder="6-digit code"
                              style={{ maxWidth: "160px" }}
                            />
                            <Button variant="primary" size="sm" onClick={confirmSms} disabled={busy}>
                              {busy ? "Checking..." : "Verify & Add"}
                            </Button>
                            <Button variant="link" size="sm" onClick={startSms} disabled={busy}>Resend</Button>
                            <Button variant="link" size="sm" onClick={cancelEnrolment}>Cancel</Button>
                          </div>
                        </div>
                      )}

                      {open && m === "totp" && totpSetup && (
                        <div className="mt-3 pt-3 border-top">
                          <Row className="align-items-center">
                            <Col md={5} className="text-center mb-3">
                              <img src={totpSetup.qr} alt="Authenticator QR code" width={180} />
                            </Col>
                            <Col md={7}>
                              <div className="alert alert-warning py-2 px-3" style={{ fontSize: "0.78rem" }}>
                                <strong>Set this up before?</strong> Delete the old
                                &ldquo;ADM One&rdquo; entry from your app first - scanning again
                                adds a second one that looks identical but no longer works.
                              </div>
                              <p className="mb-2" style={{ fontSize: "0.78rem" }}>
                                Setup key: <code>{totpSetup.secret}</code>
                              </p>
                              <div className="d-flex gap-2 flex-wrap align-items-center">
                                <Form.Control
                                  type="text"
                                  value={code}
                                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                  maxLength={6}
                                  placeholder="6-digit code"
                                  style={{ maxWidth: "150px" }}
                                />
                                <Button variant="primary" size="sm" onClick={confirmTotp} disabled={busy}>
                                  {busy ? "Checking..." : "Verify & Add"}
                                </Button>
                                <Button variant="link" size="sm" onClick={cancelEnrolment}>Cancel</Button>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <Error error={error} />
              <Success message={success} />

              {/* The only difference between the ways in is how you leave:
                  sent here after login you may postpone or log out; arriving
                  from the header badge you simply go back. */}
              <div className="d-flex gap-3 align-items-center mt-4">
                {forced ? (
                  <>
                    {hasAny && (
                      <Button variant="primary" size="sm" onClick={() => navigate(returnTo)}>
                        Continue
                      </Button>
                    )}
                    {!hasAny && canSkip && (
                      <Button variant="secondary" size="sm" onClick={handleSkip}>
                        Skip for now
                      </Button>
                    )}
                    <Button variant="link" className="p-0" onClick={handleBackToLogin}>
                      Back to login
                    </Button>
                  </>
                ) : (
                  <Button variant="link" className="p-0" onClick={() => navigate(-1)}>
                    Back
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );

  // Straight after login the app chrome is deliberately left off: the sidebar
  // and header invite the user to click away from a step they have not
  // finished, and half of it would not work before setup anyway. Reached from
  // the header badge, the page sits inside the normal layout.
  if (challengeMode && !challenge) return null; // redirecting to /login

  if (forced) {
    return (
      <div className="setup-2fa-standalone">
        <div className="text-center pt-5 pb-3">
          <img src={logo} width="220" alt="ADM One" />
        </div>
        {manageBody}
      </div>
    );
  }

  return <Default>{manageBody}</Default>;
};

export default Setup2fa;
